import React, { useState } from 'react';
import Navbar from '../Navbar';
import Footer from '../Footer';
import SEO from '../SEO';
import { Button } from '../ui/button';
import {
  supportCategories,
  supportPriorities,
  supportFaqs,
  supportChannels,
} from '../../data/supportData';
import {
  LifeBuoy,
  ChevronDown,
  CheckCircle2,
  Mail,
  Clock,
  ArrowRight,
  Loader2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const initialForm = {
  nombre: '',
  email: '',
  empresa: '',
  categoria: '',
  prioridad: 'media',
  asunto: '',
  mensaje: '',
};

const SupportPage = () => {
  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdTicket, setCreatedTicket] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const [openFaq, setOpenFaq] = useState(0);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const selectCategory = (id) => {
    setFormData((prev) => ({ ...prev, categoria: id }));
    if (errors.categoria) setErrors((prev) => ({ ...prev, categoria: '' }));
    document.getElementById('ticket-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const validate = () => {
    const errs = {};
    if (!formData.nombre.trim()) errs.nombre = 'Indica tu nombre';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'Email no válido';
    if (!formData.categoria) errs.categoria = 'Selecciona una categoría';
    if (!formData.asunto.trim()) errs.asunto = 'Indica un asunto';
    if (!formData.mensaje.trim()) errs.mensaje = 'Describe tu solicitud';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/support/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error('Error al crear el ticket');
      const ticket = await response.json();
      setCreatedTicket(ticket);
    } catch (err) {
      setSubmitError('No pudimos crear tu ticket. Inténtalo de nuevo o escríbenos a soporte@asisant.com.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData(initialForm);
    setCreatedTicket(null);
    setErrors({});
  };

  const inputClass = (field) =>
    `w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:border-[#1297fd] transition-colors ${
      errors[field] ? 'border-red-400' : 'border-[#e3e4e6]'
    }`;

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Centro de Soporte — Marketplace, vendors y facturación"
        description="Soporte para clientes de asisant: crea tickets de Marketplace, integraciones con vendors, facturación, APM, incidencias técnicas y solicitudes generales."
        keywords="soporte asisant, ayuda marketplace, soporte integraciones vendors, ticket soporte cloud"
        canonical="/soporte"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: supportFaqs.map((f) => ({
            '@type': 'Question',
            name: f.question,
            acceptedAnswer: { '@type': 'Answer', text: f.answer },
          })),
        }}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative bg-dark-ellipse pt-32 pb-20 overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-white/20">
            <LifeBuoy className="w-4 h-4 text-[#8bcaf9]" />
            <span className="text-white text-sm font-medium">Centro de Soporte</span>
          </div>
          <h1 data-testid="support-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            ¿En qué podemos <span className="text-[#8bcaf9]">ayudarte?</span>
          </h1>
          <p className="text-lg sm:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
            Selecciona la categoría de tu solicitud y crea un ticket. Nuestro equipo te responde
            en menos de 24 horas hábiles.
          </p>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">
              Categorías
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Elige el tipo de <span className="text-[#1297fd]">solicitud</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {supportCategories.map((cat) => {
              const Icon = cat.icon;
              const isSelected = formData.categoria === cat.id;
              return (
                <button
                  key={cat.id}
                  type="button"
                  data-testid={`support-category-${cat.id}`}
                  onClick={() => selectCategory(cat.id)}
                  className={`text-left rounded-2xl border-2 p-6 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${
                    isSelected
                      ? 'border-[#1297fd] bg-[#8bcaf9]/10 shadow-lg'
                      : 'border-[#e3e4e6] bg-white hover:border-[#8bcaf9]'
                  }`}
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 text-lg mb-2">{cat.name}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">{cat.description}</p>
                  <ul className="space-y-1.5 mb-4">
                    {cat.examples.map((ex) => (
                      <li key={ex} className="flex items-center gap-2 text-xs text-gray-400">
                        <span className="w-1 h-1 rounded-full bg-[#4E95D9]" />
                        {ex}
                      </li>
                    ))}
                  </ul>
                  <span className={`inline-flex items-center gap-1 text-sm font-semibold ${isSelected ? 'text-[#1297fd]' : 'text-[#4E95D9]'}`}>
                    {isSelected ? 'Seleccionada' : 'Crear ticket'}
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ticket form */}
      <section id="ticket-form" className="py-20 bg-[#f6f8fb]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {createdTicket ? (
            <div data-testid="support-ticket-success" className="bg-white rounded-3xl p-12 shadow-xl text-center border border-[#e3e4e6]">
              <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center bg-green-100">
                <CheckCircle2 className="w-10 h-10 text-green-500" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">¡Ticket creado correctamente!</h3>
              <p className="text-gray-500 mb-6">
                Tu número de referencia es{' '}
                <span data-testid="support-ticket-number" className="font-bold text-[#1297fd]">{createdTicket.ticket_number}</span>.
                Te responderemos a <span className="font-semibold">{createdTicket.email}</span> en menos de 24 horas hábiles.
              </p>
              <Button
                data-testid="support-new-ticket-btn"
                onClick={resetForm}
                className="text-white font-semibold px-8 py-6 rounded-full"
                style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}
              >
                Crear otro ticket
              </Button>
            </div>
          ) : (
            <>
              <div className="text-center mb-10">
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                  Crea tu <span className="text-[#1297fd]">ticket de soporte</span>
                </h2>
                <p className="text-gray-500">Completa el formulario y recibirás un número de seguimiento.</p>
              </div>

              <form onSubmit={handleSubmit} className="bg-white rounded-3xl border border-[#e3e4e6] p-8 shadow-xl space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Nombre *</label>
                    <input
                      data-testid="support-input-nombre"
                      type="text"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleChange}
                      placeholder="Tu nombre completo"
                      className={inputClass('nombre')}
                    />
                    {errors.nombre && <p className="text-red-500 text-xs mt-1">{errors.nombre}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                    <input
                      data-testid="support-input-email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="tu@empresa.com"
                      className={inputClass('email')}
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Empresa</label>
                    <input
                      data-testid="support-input-empresa"
                      type="text"
                      name="empresa"
                      value={formData.empresa}
                      onChange={handleChange}
                      placeholder="Nombre de tu empresa"
                      className={inputClass('empresa')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Categoría *</label>
                    <select
                      data-testid="support-select-categoria"
                      name="categoria"
                      value={formData.categoria}
                      onChange={handleChange}
                      className={`${inputClass('categoria')} appearance-none bg-white`}
                    >
                      <option value="">Selecciona una categoría</option>
                      {supportCategories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    {errors.categoria && <p className="text-red-500 text-xs mt-1">{errors.categoria}</p>}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Prioridad</label>
                    <select
                      data-testid="support-select-prioridad"
                      name="prioridad"
                      value={formData.prioridad}
                      onChange={handleChange}
                      className={`${inputClass('prioridad')} appearance-none bg-white`}
                    >
                      {supportPriorities.map((p) => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Asunto *</label>
                    <input
                      data-testid="support-input-asunto"
                      type="text"
                      name="asunto"
                      value={formData.asunto}
                      onChange={handleChange}
                      placeholder="Resumen breve de tu solicitud"
                      className={inputClass('asunto')}
                    />
                    {errors.asunto && <p className="text-red-500 text-xs mt-1">{errors.asunto}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Descripción *</label>
                  <textarea
                    data-testid="support-input-mensaje"
                    name="mensaje"
                    value={formData.mensaje}
                    onChange={handleChange}
                    rows={5}
                    placeholder="Describe tu solicitud con el mayor detalle posible (números de pedido, facturas, vendor afectado, mensajes de error...)"
                    className={`${inputClass('mensaje')} resize-none`}
                  />
                  {errors.mensaje && <p className="text-red-500 text-xs mt-1">{errors.mensaje}</p>}
                </div>

                {submitError && (
                  <p data-testid="support-submit-error" className="text-red-500 text-sm">{submitError}</p>
                )}

                <Button
                  data-testid="support-submit-btn"
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full text-white font-semibold py-6 rounded-full text-lg transition-all hover:scale-[1.02] hover:shadow-xl"
                  style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    'Crear ticket de soporte'
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </section>

      {/* Support FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">
              FAQ de Soporte
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Preguntas <span className="text-[#1297fd]">frecuentes</span>
            </h2>
          </div>

          <div className="space-y-4">
            {supportFaqs.map((item, idx) => (
              <div
                key={idx}
                className={`rounded-2xl border-2 transition-all duration-300 overflow-hidden ${
                  openFaq === idx
                    ? 'border-[#8bcaf9] bg-[#8bcaf9]/10 shadow-lg'
                    : 'border-[#e3e4e6] bg-white hover:border-[#c3c3c5]'
                }`}
              >
                <button
                  data-testid={`support-faq-${idx}`}
                  onClick={() => setOpenFaq(openFaq === idx ? -1 : idx)}
                  className="w-full p-6 text-left flex items-center justify-between gap-4"
                >
                  <span className={`font-semibold text-lg transition-colors ${openFaq === idx ? 'text-[#1297fd]' : 'text-gray-900'}`}>
                    {item.question}
                  </span>
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${openFaq === idx ? 'rotate-180' : 'bg-[#e3e4e6]'}`}
                    style={openFaq === idx ? { background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' } : {}}
                  >
                    <ChevronDown className={`w-5 h-5 transition-colors ${openFaq === idx ? 'text-white' : 'text-gray-600'}`} />
                  </div>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === idx ? 'max-h-96' : 'max-h-0'}`}>
                  <div className="px-6 pb-6">
                    <p className="text-gray-500 leading-relaxed">{item.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Direct channels */}
      <section className="py-20 bg-dark-ellipse">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Canales de <span className="text-[#8bcaf9]">contacto directo</span>
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              Si prefieres no usar el formulario, también puedes escribirnos directamente.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {supportChannels.map((ch) => (
              <div
                key={ch.title}
                data-testid={`support-channel-${ch.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 text-center hover:bg-white/15 transition-all hover:scale-105"
              >
                <div className="w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center bg-white/10 border border-white/20">
                  {ch.type === 'email' ? (
                    <Mail className="w-6 h-6 text-[#8bcaf9]" />
                  ) : (
                    <Clock className="w-6 h-6 text-[#8bcaf9]" />
                  )}
                </div>
                <h3 className="text-white font-bold mb-2">{ch.title}</h3>
                {ch.href ? (
                  <a href={ch.href} className="text-[#8bcaf9] font-semibold hover:underline break-all">
                    {ch.value}
                  </a>
                ) : (
                  <p className="text-[#8bcaf9] font-semibold">{ch.value}</p>
                )}
                <p className="text-white/60 text-sm mt-2">{ch.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SupportPage;
